import { useEffect, type ReactNode } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useAuthStore } from './stores/authStore';

// Landing page
import LandingPage from './pages/LandingPage';
import CreatorCardLanding from './pages/CreatorCardLanding';
import Features from './pages/Features';
import Pricing from './pages/Pricing';
import About from './pages/About';
import Contact from './pages/Contact';
import Blog from './pages/Blog';
import Careers from './pages/Careers';
import Help from './pages/Help';
import Support from './pages/Support';
import Privacy from './pages/Privacy';
import CreatorData from './pages/CreatorData';
import Terms from './pages/Terms';
import Cookies from './pages/Cookies';
import Compliance from './pages/Compliance';
import NotFound from './pages/NotFound';
import OrgOnboardingPage from './pages/onboarding/OrgOnboardingPage';

// Admin pages
import AdminLayout from './components/layouts/AdminLayout';
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminUsers from './pages/admin/AdminUsers';
import AdminKOLs from './pages/admin/AdminKOLs';
import AdminBrands from './pages/admin/AdminBrands';
import AdminAgencies from './pages/admin/AdminAgencies';
import AdminAgencyDetail from './pages/admin/AdminAgencyDetail';
import AdminCampaigns from './pages/admin/AdminCampaigns';
import AdminPayments from './pages/admin/AdminPayments';
import AdminAiCredits from './pages/admin/AdminAiCredits';
import AdminInvitationsPage from './pages/admin/AdminInvitationsPage';
import AdminChatbotLog from './pages/admin/AdminChatbotLog';
import AdminSecurity from './pages/admin/AdminSecurity';
import AdminDataSources from './pages/admin/AdminDataSources';
import AdminGrowth from './pages/admin/AdminGrowth';
import AccountSuspended from './pages/AccountSuspended';

// Auth pages
import LoginPage from './pages/auth/LoginPage';
import RegisterPage from './pages/auth/RegisterPage';
import ForgotPasswordPage from './pages/auth/ForgotPasswordPage';
import ResetPasswordPage from './pages/auth/ResetPasswordPage';
import ActionSsoPage from './pages/auth/ActionSsoPage';
import AuthCallbackPage from './pages/auth/AuthCallbackPage';
import SocialSignupCompletePage from './pages/auth/SocialSignupCompletePage';
import SupabaseHashRedirect from './components/SupabaseHashRedirect';

// Agency pages
import AgencyLayout from './components/layouts/AgencyLayout';
import AgencyDashboard from './pages/agency/Dashboard';
import AgencyAnalytics from './pages/agency/Analytics';
import AuditLogs from './pages/agency/AuditLogs';
import BrandsPage from './pages/agency/Brands';
import AgencyBrandDetail from './pages/agency/BrandDetail';
import AgencyBrandTeam from './pages/agency/BrandTeam';
import KOLsPage from './pages/agency/KOLs';
import ExplorePage from './pages/agency/Explore';
import KOLBulkImport from './pages/agency/KOLBulkImport';
import KOLsTableEdit from './pages/agency/KOLsTableEdit';
import KOLDetailPage from './pages/agency/KOLDetail';
import JobsPage from './pages/agency/Jobs';
import JobDetailPage from './pages/agency/JobDetail';
import CreateJobPage from './pages/agency/CreateJob';
import AgencyJobsRedirect from './pages/agency/AgencyJobsRedirect';
import JobTemplatesPage from './pages/agency/JobTemplates';
import ContractTemplatesPage from './pages/agency/ContractTemplates';
import WebhooksPage from './pages/agency/Webhooks';
import KolPerformanceReport from './pages/agency/KolPerformanceReport';
import KOLDataTools from './pages/agency/KOLDataTools';
import PaymentsAnalytics from './pages/agency/PaymentsAnalytics';
import AgencyReports from './pages/agency/Reports';
import AgencyTagsPage from './pages/agency/Tags';
import MyAssignmentsPage from './pages/agency/MyAssignments';
import InvitationDetailPage from './pages/agency/InvitationDetail';
import AIDemo from './pages/agency/AIDemo';
import AIActionPlans from './pages/agency/AIActionPlans';
import ScriptDetailPage from './pages/agency/ScriptDetail';
import AgencyScripts from './pages/agency/AgencyScripts';
import SubmissionDetailPage from './pages/agency/SubmissionDetail';
import AgencySubmissions from './pages/agency/AgencySubmissions';
import CreativeCutsPage from './pages/agency/CreativeCuts';
import CreativeCutsNewPage from './pages/agency/CreativeCutsNew';
import CreativeCutsBatchPage from './pages/agency/CreativeCutsBatch';
import AgencyAiCredits from './pages/agency/AgencyAiCredits';
import AgencyAnimationStudio from './pages/agency/AgencyAnimationStudio';
import AgencyGallery from './pages/agency/AgencyGallery';

// Brand portal pages
import BrandLayout from './components/layouts/BrandLayout';
import CorporateDashboard from './pages/brand/Dashboard';
import CorporateAnalytics from './pages/brand/Analytics';
import CorporateJobs from './pages/brand/Jobs';
import CorporateJobDetail from './pages/brand/JobDetail';
import CorporateKOLs from './pages/brand/KOLs';
import CorporateKOLDetail from './pages/brand/KOLDetail';
import CorporateScripts from './pages/brand/Scripts';
import CorporateScriptDetail from './pages/brand/ScriptDetail';
import CorporateSubmissions from './pages/brand/Submissions';
import CorporateSubmissionDetail from './pages/brand/SubmissionDetail';
import CorporateTeam from './pages/brand/Team';
import CorporateMessages from './pages/brand/Messages';
import CorporatePayments from './pages/brand/Payments';
import BrandReports from './pages/brand/Reports';

// KOL pages
import KOLLayout from './components/layouts/KOLLayout';
import KOLDashboard from './pages/kol/Dashboard';
import KOLAnalytics from './pages/kol/Analytics';
import KOLInvitations from './pages/kol/Invitations';
import KOLInvitationDetail from './pages/kol/InvitationDetail';
import InviteLanding from './pages/kol/InviteLanding';
import KOLScripts from './pages/kol/Scripts';
import KOLScriptDetail from './pages/kol/ScriptDetail';
import KOLSubmissions from './pages/kol/Submissions';
import KOLSubmissionDetail from './pages/kol/SubmissionDetail';
import ActionLinkLanding from './pages/kol/ActionLinkLanding';
import KOLProfileEdit from './pages/kol/KOLProfileEdit';
import KOLContracts from './pages/kol/Contracts';
import InstagramCallback from './pages/kol/InstagramCallback';
import TaxSubmission from './pages/kol/TaxSubmission';
import KOLMessages from './pages/kol/Messages';
import KOLEarnings from './pages/kol/Earnings';

// Common pages
import Profile from './pages/common/Profile';
import TeamManagement from './pages/agency/Team';
import FAQ from './pages/FAQ';
import Notifications from './pages/common/Notifications';
import ContractsOverviewPage from './pages/common/ContractsOverview';
import GuidesLegacyRedirect from './pages/guides/GuidesLegacyRedirect';
import AgencyGuidePage from './pages/agency/AgencyGuidePage';
import AgencyMessagingIntegrations from './pages/agency/MessagingIntegrations';
import AgencyWhatsAppInbox from './pages/agency/AgencyWhatsAppInbox';
import AdminMessagingIntegrations from './pages/admin/AdminMessagingIntegrations';
import AgencyMessagingLog from './pages/agency/MessagingLog';
import AgencyInvitationsPage from './pages/agency/AgencyInvitationsPage';
import AgencyApiDocs from './pages/agency/AgencyApiDocs';
import AgencyWhatsAppMock from './pages/agency/AgencyWhatsAppMock';
import WhatsAppAssignmentCanvas from './pages/agency/WhatsAppAssignmentCanvas';
import AdminMessagingLog from './pages/admin/AdminMessagingLog';
import AgencyMessagingTemplates from './pages/agency/MessagingTemplates';
import GoogleWorkspaceSettingsPage from './pages/agency/GoogleWorkspaceSettings';
import AdminMessagingTemplates from './pages/admin/AdminMessagingTemplates';
import AdminPlunkWebhookGuide from './pages/admin/AdminPlunkWebhookGuide';
import ExtensionConnectPage from './pages/extension/ExtensionConnectPage';
import ExtensionDevices from './pages/agency/ExtensionDevices';

// Protected route wrapper
import ProtectedRoute from './components/ProtectedRoute';
import RealtimeBridge from './components/RealtimeBridge';
import ThemeBridge from './components/ThemeBridge';
import HelpChatWidget from './components/help/HelpChatWidget';
import { sanitizeAppRedirect } from './lib/inviteRedirect';

function authenticatedLandingPath(user: NonNullable<ReturnType<typeof useAuthStore.getState>['user']>): string {
  const redirect =
    typeof window !== 'undefined'
      ? sanitizeAppRedirect(new URLSearchParams(window.location.search).get('redirect'))
      : '';
  if (redirect) return redirect;
  if (user.role === 'AGENCY') return '/agency/dashboard';
  if (user.role === 'BRAND') return '/brand/dashboard';
  if (user.role === 'ADMIN') return '/admin/dashboard';
  return '/kol/dashboard';
}

function AuthRedirectRoute({ children }: { children: ReactNode }) {
  const { fetchUser, hasCheckedSession, isLoading, user } = useAuthStore();
  useEffect(() => {
    if (!hasCheckedSession && !isLoading) {
      void fetchUser({ silent: true });
    }
  }, [fetchUser, hasCheckedSession, isLoading]);

  if (user) {
    return <Navigate to={authenticatedLandingPath(user)} replace />;
  }

  return <>{children}</>;
}

function App() {
  const user = useAuthStore((state) => state.user);

  return (
    <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <SupabaseHashRedirect />
      <>
        <ThemeBridge />
        {user && <RealtimeBridge />}
        {user && <HelpChatWidget />}
        <Routes>
          {/* Public routes */}
          <Route path="/" element={<LandingPage />} />
          <Route path="/features" element={<Features />} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/about" element={<About />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/blog" element={<Blog />} />
          <Route path="/careers" element={<Careers />} />
          <Route path="/help" element={<Help />} />
          <Route
            path="/guides"
            element={
              <ProtectedRoute>
                <GuidesLegacyRedirect />
              </ProtectedRoute>
            }
          />
          <Route
            path="/guides/:role"
            element={
              <ProtectedRoute>
                <GuidesLegacyRedirect />
              </ProtectedRoute>
            }
          />
          <Route path="/support" element={<Support />} />
          <Route path="/privacy" element={<Privacy />} />
          {/* Reached by creators who are not users and must not need an account to get here. */}
          <Route path="/creator-data" element={<CreatorData />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/cookies" element={<Cookies />} />
          <Route path="/compliance" element={<Compliance />} />
          <Route path="/faq" element={<FAQ />} />
          <Route path="/creator-card" element={<CreatorCardLanding />} />
          <Route path="/invite/:id" element={<InviteLanding />} />
          <Route
            path="/login"
            element={
              <AuthRedirectRoute>
                <LoginPage />
              </AuthRedirectRoute>
            }
          />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/auth/callback" element={<AuthCallbackPage />} />
          <Route path="/auth/social-complete" element={<SocialSignupCompletePage />} />
          <Route
            path="/onboarding/organization"
            element={
              <ProtectedRoute allowedRoles={['AGENCY', 'BRAND']}>
                <OrgOnboardingPage />
              </ProtectedRoute>
            }
          />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/sso/action" element={<ActionSsoPage />} />
          <Route path="/sso/action-ready" element={<ActionLinkLanding />} />
          <Route path="/404" element={<NotFound />} />
          <Route path="/account-suspended" element={<AccountSuspended />} />
          <Route
            path="/extension/connect"
            element={
              <ProtectedRoute allowedRoles={['AGENCY', 'ADMIN']}>
                <ExtensionConnectPage />
              </ProtectedRoute>
            }
          />

        {/* Agency routes */}
        <Route
          path="/agency"
          element={
            <ProtectedRoute allowedRoles={['AGENCY']}>
              <AgencyLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<Navigate to="/agency/dashboard" replace />} />
          <Route path="dashboard" element={<AgencyDashboard />} />
          <Route path="analytics" element={<AgencyAnalytics />} />
          <Route path="payments-analytics" element={<PaymentsAnalytics />} />
          <Route path="reports" element={<AgencyReports />} />
          <Route path="audit" element={<AuditLogs />} />
          <Route path="profile" element={<Profile />} />
          <Route path="team" element={<TeamManagement />} />
          <Route path="brands" element={<BrandsPage />} />
          <Route path="brands/:id" element={<AgencyBrandDetail />} />
          <Route path="brands/:id/team" element={<AgencyBrandTeam />} />
          <Route path="kols" element={<KOLsPage />} />
          <Route path="explore" element={<ExplorePage />} />
          <Route path="kols/bulk-import" element={<KOLBulkImport />} />
          <Route path="kols/table" element={<KOLsTableEdit />} />
          <Route path="kol-performance" element={<KolPerformanceReport />} />
          <Route path="kol-data-tools" element={<KOLDataTools />} />
          <Route path="extension-devices" element={<ExtensionDevices />} />
          <Route path="tags" element={<AgencyTagsPage />} />
          <Route path="kols/:id" element={<KOLDetailPage />} />
          <Route path="kols/:id/edit" element={<KOLProfileEdit />} />
          <Route path="campaigns" element={<JobsPage />} />
          <Route path="campaigns/new" element={<CreateJobPage />} />
          <Route path="campaigns/:id/edit" element={<CreateJobPage />} />
          <Route path="jobs" element={<AgencyJobsRedirect />} />
          <Route path="jobs/new" element={<AgencyJobsRedirect />} />
          <Route path="jobs/:id/edit" element={<AgencyJobsRedirect />} />
          <Route path="job-templates" element={<JobTemplatesPage />} />
          <Route path="contract-templates" element={<ContractTemplatesPage />} />
          <Route path="messaging-templates" element={<AgencyMessagingTemplates />} />
          <Route path="contracts" element={<ContractsOverviewPage role="AGENCY" />} />
          <Route path="webhooks" element={<WebhooksPage />} />
          <Route path="messaging" element={<AgencyMessagingIntegrations />} />
          <Route path="google-workspace" element={<GoogleWorkspaceSettingsPage />} />
          <Route path="inbox" element={<AgencyWhatsAppInbox />} />
          <Route path="messaging-log" element={<AgencyMessagingLog />} />
          <Route path="api-docs" element={<AgencyApiDocs />} />
          <Route path="whatsapp-mock" element={<AgencyWhatsAppMock />} />
          <Route path="whatsapp-canvas" element={<WhatsAppAssignmentCanvas />} />
          <Route path="campaigns/:id" element={<JobDetailPage />} />
          <Route path="jobs/:id" element={<AgencyJobsRedirect />} />
          <Route path="my-assignments" element={<MyAssignmentsPage />} />
          <Route path="invitations" element={<AgencyInvitationsPage />} />
          <Route path="invitations/:id" element={<InvitationDetailPage />} />
          <Route path="ai-action-plans" element={<AIActionPlans />} />
          <Route path="ai-demo" element={<AIDemo />} />
          <Route path="scripts" element={<AgencyScripts />} />
          <Route path="scripts/:id" element={<ScriptDetailPage />} />
          <Route path="submissions" element={<AgencySubmissions />} />
          <Route path="submissions/:id" element={<SubmissionDetailPage />} />
          <Route path="ai-video-edits" element={<CreativeCutsPage />} />
          <Route path="ai-video-edits/new" element={<CreativeCutsNewPage />} />
          <Route path="ai-video-edits/batch/:batchId" element={<CreativeCutsBatchPage />} />
          <Route path="creative-cuts" element={<Navigate to="/agency/ai-video-edits" replace />} />
          <Route path="creative-cuts/new" element={<Navigate to="/agency/ai-video-edits/new" replace />} />
          <Route path="animation-studio" element={<AgencyAnimationStudio />} />
          <Route path="gallery" element={<AgencyGallery />} />
          <Route path="remotion-studio" element={<Navigate to="/agency/animation-studio" replace />} />
          <Route path="ai-credits" element={<AgencyAiCredits />} />
          <Route path="faq" element={<FAQ />} />
          <Route path="notifications" element={<Notifications />} />
          <Route path="guide" element={<AgencyGuidePage />} />
        </Route>

        {/* Brand portal routes */}
        <Route
          path="/brand"
          element={
            <ProtectedRoute allowedRoles={['BRAND']}>
              <BrandLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<Navigate to="/brand/dashboard" replace />} />
          <Route path="dashboard" element={<CorporateDashboard />} />
          <Route path="analytics" element={<CorporateAnalytics />} />
          <Route path="profile" element={<Profile />} />
          <Route path="team" element={<CorporateTeam />} />
          <Route path="jobs" element={<CorporateJobs />} />
          <Route path="jobs/new" element={<CreateJobPage />} />
          <Route path="jobs/:id/edit" element={<CreateJobPage />} />
          <Route path="contracts" element={<ContractsOverviewPage role="BRAND" />} />
          <Route path="jobs/:id" element={<CorporateJobDetail />} />
          <Route path="kols" element={<CorporateKOLs />} />
          <Route path="kols/:id" element={<CorporateKOLDetail />} />
          <Route path="scripts" element={<CorporateScripts />} />
          <Route path="scripts/:id" element={<CorporateScriptDetail />} />
          <Route path="submissions" element={<CorporateSubmissions />} />
          <Route path="submissions/:id" element={<CorporateSubmissionDetail />} />
          <Route path="messages" element={<CorporateMessages />} />
          <Route path="payments" element={<CorporatePayments />} />
          <Route path="reports" element={<BrandReports />} />
          <Route path="faq" element={<FAQ />} />
          <Route path="notifications" element={<Notifications />} />
          <Route path="guide" element={<Navigate to="/brand/dashboard" replace />} />
        </Route>

        {/* KOL routes */}
        <Route
          path="/kol"
          element={
            <ProtectedRoute allowedRoles={['KOL']}>
              <KOLLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<Navigate to="/kol/dashboard" replace />} />
          <Route path="dashboard" element={<KOLDashboard />} />
          <Route path="analytics" element={<KOLAnalytics />} />
          <Route path="account" element={<Profile />} />
          <Route path="profile" element={<Navigate to="/kol/my-profile" replace />} />
          <Route path="profile/:id" element={<KOLProfileEdit />} />
          <Route path="my-profile" element={<KOLProfileEdit />} />
          <Route path="instagram-callback" element={<InstagramCallback />} />
          <Route path="tax" element={<TaxSubmission />} />
          <Route path="invitations" element={<KOLInvitations />} />
          <Route path="invitations/:id" element={<KOLInvitationDetail />} />
          <Route path="scripts" element={<KOLScripts />} />
          <Route path="scripts/:id" element={<KOLScriptDetail />} />
          <Route path="submissions" element={<KOLSubmissions />} />
          <Route path="submissions/:id" element={<KOLSubmissionDetail />} />
          <Route path="contracts" element={<KOLContracts />} />
          <Route path="messages" element={<KOLMessages />} />
          <Route path="payments" element={<KOLEarnings />} />
          <Route path="earnings" element={<KOLEarnings />} />
          <Route path="faq" element={<FAQ />} />
          <Route path="notifications" element={<Notifications />} />
          <Route path="guide" element={<Navigate to="/kol/dashboard" replace />} />
        </Route>

        {/* Admin routes */}
        <Route
          path="/admin"
          element={
            <ProtectedRoute allowedRoles={['ADMIN']}>
              <AdminLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<Navigate to="/admin/dashboard" replace />} />
          <Route path="dashboard" element={<AdminDashboard />} />
          <Route path="users" element={<AdminUsers />} />
          <Route path="kols" element={<AdminKOLs />} />
          <Route path="brands" element={<AdminBrands />} />
          <Route path="agencies" element={<AdminAgencies />} />
          <Route path="agencies/:agencyId" element={<AdminAgencyDetail />} />
          <Route path="ai-credits" element={<AdminAiCredits />} />
          <Route path="campaigns" element={<AdminCampaigns />} />
          <Route path="contracts" element={<ContractsOverviewPage role="ADMIN" />} />
          <Route path="payments" element={<AdminPayments />} />
          <Route path="messaging" element={<AdminMessagingIntegrations />} />
          <Route path="messaging-log" element={<AdminMessagingLog />} />
          <Route path="invitations" element={<AdminInvitationsPage />} />
          <Route path="invitations/:id" element={<InvitationDetailPage />} />
          <Route path="messaging-templates" element={<AdminMessagingTemplates />} />
          <Route path="plunk-webhooks" element={<AdminPlunkWebhookGuide />} />
          <Route path="chatbot-log" element={<AdminChatbotLog />} />
          <Route path="security" element={<AdminSecurity />} />
          <Route path="data-sources" element={<AdminDataSources />} />
          <Route path="growth" element={<AdminGrowth />} />
          <Route path="profile" element={<Profile />} />
          <Route path="faq" element={<FAQ />} />
          <Route path="notifications" element={<Notifications />} />
        </Route>

        {/* Default redirect based on role */}
        <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </>
    </BrowserRouter>
  );
}

export default App;

